import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { trackEvent } from "../lib/ga";

const API = process.env.REACT_APP_BACKEND_URL;

const FORUMS = [
  "FINRA Dispute Resolution arbitration",
  "AAA / JAMS arbitration",
  "State court",
  "Federal court",
  "SEC / FINRA enforcement or investigation",
  "Other",
];
const SIDES = ["Claimant", "Respondent", "Other / Not yet determined"];
const DISCOVERY = ["Not open yet", "Currently open", "Discovery has closed"];

const field = "w-full border border-[#0A2B3D]/20 px-4 py-3 text-sm text-[#0A2B3D] focus:border-[#3E9BDC] focus:outline-none transition-colors";
const label = "block text-xs font-mono uppercase tracking-[0.12em] text-[#475569] mb-2";

export const ExpertIntakeForm = () => {
  const empty = {
    law_firm: "", attorney_name: "", attorney_email: "", office_phone: "", cell_phone: "",
    case_name: "", forum: FORUMS[0], representing: "", claim_types: "", jurisdiction: "",
    case_number: "", discovery_status: "", hearing_date: "", notes: "",
  };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const started = useRef(false);

  const onFirstInteract = () => {
    if (started.current) return;
    started.current = true;
    trackEvent("form_start", { form: "Expert Witness Intake", source_page: window.location.pathname });
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/api/expert-intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source_page: window.location.pathname }),
      });
      if (!res.ok) throw new Error("Request failed");
      setSent(true);
      trackEvent("generate_lead", { service: "Expert Witness Services", source_page: window.location.pathname });
      toast.success("Your inquiry has been received.");
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white p-10 border border-[#0A2B3D]/10 shadow-xl text-center" data-testid="intake-success-message">
        <ShieldCheck size={40} className="text-[#3E9BDC] mx-auto" />
        <h3 className="mt-5 font-serif font-bold text-2xl text-[#0A2B3D]">Inquiry received.</h3>
        <p className="mt-3 text-[#475569] leading-relaxed">
          Thank you. We will run a conflict check and follow up to discuss scope and next steps.
          Please do not send privileged or non-public materials until a signed engagement letter and
          retainer are in place.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} onFocusCapture={onFirstInteract} className="bg-white p-8 md:p-10 border border-[#0A2B3D]/10 shadow-xl" data-testid="expert-intake-form">
      <div className="flex items-start gap-3 bg-[#EBF3F8] border border-[#A9CFE2]/60 p-4 mb-8" data-testid="intake-confidentiality">
        <ShieldCheck size={18} className="text-[#3E9BDC] mt-0.5 shrink-0" />
        <p className="text-xs text-[#475569] leading-relaxed">
          Information submitted here is held in confidence. <strong>Public information only.</strong> Please
          do not submit case strategy, privileged communications, other expert opinions, or non-public
          documents until a signed engagement letter and retainer are in place.
        </p>
      </div>

      <p className="eyebrow text-[#3E9BDC] mb-4">Contact Information</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div><label className={label}>Law Firm Name *</label><input required className={field} value={form.law_firm} onChange={set("law_firm")} data-testid="intake-firm-input" /></div>
        <div><label className={label}>Attorney Name *</label><input required className={field} value={form.attorney_name} onChange={set("attorney_name")} data-testid="intake-attorney-input" /></div>
        <div><label className={label}>Attorney Email *</label><input required type="email" className={field} value={form.attorney_email} onChange={set("attorney_email")} data-testid="intake-email-input" /></div>
        <div><label className={label}>Office Phone</label><input className={field} value={form.office_phone} onChange={set("office_phone")} data-testid="intake-office-phone-input" /></div>
        <div><label className={label}>Cell Phone</label><input className={field} value={form.cell_phone} onChange={set("cell_phone")} data-testid="intake-cell-phone-input" /></div>
      </div>

      <p className="eyebrow text-[#3E9BDC] mb-4 mt-9">Matter Information</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2"><label className={label}>Case / Matter Name</label><input className={field} value={form.case_name} onChange={set("case_name")} data-testid="intake-case-input" /></div>
        <div>
          <label className={label}>Forum / Proceeding Type</label>
          <select className={field} value={form.forum} onChange={set("forum")} data-testid="intake-forum-select">
            {FORUMS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Representing</label>
          <select className={field} value={form.representing} onChange={set("representing")} data-testid="intake-representing-select">
            <option value="">Select…</option>
            {SIDES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2"><label className={label}>Claim Types (e.g., suitability, supervision, unauthorized trading)</label><input className={field} value={form.claim_types} onChange={set("claim_types")} data-testid="intake-claims-input" /></div>
        <div><label className={label}>Jurisdiction (if applicable)</label><input className={field} value={form.jurisdiction} onChange={set("jurisdiction")} data-testid="intake-jurisdiction-input" /></div>
        <div><label className={label}>Case Number (if applicable)</label><input className={field} value={form.case_number} onChange={set("case_number")} data-testid="intake-casenumber-input" /></div>
        <div>
          <label className={label}>Discovery Status</label>
          <select className={field} value={form.discovery_status} onChange={set("discovery_status")} data-testid="intake-discovery-select">
            <option value="">Select…</option>
            {DISCOVERY.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div><label className={label}>Hearing / Key Deadline</label><input type="date" className={field} value={form.hearing_date} onChange={set("hearing_date")} data-testid="intake-hearing-input" /></div>
        <div className="sm:col-span-2"><label className={label}>Notes (public information only)</label><textarea rows={4} className={field} value={form.notes} onChange={set("notes")} data-testid="intake-notes-input" /></div>
      </div>

      <button type="submit" disabled={sending} className="btn-primary mt-8 w-full sm:w-auto justify-center" data-testid="intake-submit-button">
        {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Request a Pre-Retention Review"}
      </button>
    </form>
  );
};
